import React from 'react';

import SnackbarM, {
  SnackbarProps as SnackbarPropsM,
} from '@mui/material/Snackbar';

import { SnackbarContent } from './SnackbarContent.jsx';

export interface SnackbarProps extends Omit<
  SnackbarPropsM,
  'action' | 'message'
> {
  /**
   * If true, the component is shown.
   */
  open?: boolean;
  /**
   * If true, the undo button is shown.
   */
  undo?: boolean;
  /**
   * The action to display. It renders after the message, at the end of the snackbar.

   */
  actions?: React.ReactNode;
  /**
   * The number of milliseconds to wait before automatically calling the onClose function.
   * onClose should then set the state of the open prop to hide the Snackbar.
   * This behavior is disabled by default with the null value.

   */
  autoHideDuration?: number;
  /**
   * Override or extend the styles applied to the component.

   */
  classes?: SnackbarPropsM['classes'];
  /**
   * Props applied to the ClickAwayListener element.

   */
  ClickAwayListenerProps?: SnackbarPropsM['ClickAwayListenerProps'];
  /**
   * Props applied to the SnackbarContent element.

   */
  ContentProps?: SnackbarPropsM['ContentProps'];
  /**
   * If true, the autoHideDuration timer will expire even if the window is not focused.

   */
  disableWindowBlurListener?: boolean;
  /**
   * When displaying multiple consecutive Snackbars from a parent rendering a single <Snackbar/>, add the key prop to ensure independent treatment of each message.
   * e.g. <Snackbar key={message} />, otherwise, the message may update-in-place and features such as autoHideDuration may be canceled.

   */
  key?: SnackbarPropsM['key'];
  /**
   * The number of milliseconds to wait before dismissing after user interaction. If autoHideDuration prop isn't specified, it does nothing.
   * If autoHideDuration prop is specified but resumeHideDuration isn't, we default to autoHideDuration / 2 ms.

   */
  resumeHideDuration?: number;
  /**
   * The component used for the transition. Follow this guide to learn more about the requirements for this component.

   */
  TransitionComponent?: SnackbarPropsM['TransitionComponent'];
  /**
   * The duration for the transition, in milliseconds.
   * You may specify a single timeout for all transitions, or individually with an object.
   */
  // transitionDuration: PropTypes.number | { appear?: number, enter?: number, exit?: number },
  /**
   * Props applied to the transition element. By default, the element is based on this Transition component.

   */
  TransitionProps?: SnackbarPropsM['TransitionProps'];
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.

   */
  sx?: SnackbarPropsM['sx'];
  /**
   * Variant of the text layout
   * @default multiline
   */
  textLayout?: 'inline' | 'multiline';
  /**
   * Snackbar's subtitle
   */
  subTitle?: React.ReactNode;
  /**
   * removable
   */
  removable?: boolean;
  /**
   * The severity of the snackbar. This defines the color and icon used.
   */
  severity?: 'success' | 'info' | 'warning' | 'error';
}

const Snackbar: React.FC<SnackbarProps> = ({
  title,
  subTitle,
  textLayout = 'inline',
  severity,
  actions,
  removable,
  onClose,
  ...props
}) => {
  return (
    <SnackbarM {...props}>
      <SnackbarContent
        {...{
          textLayout,
          severity,
          subTitle,
          actions,
          title,
          onClose,
          removable,
        }}
      />
    </SnackbarM>
  );
};

export default Snackbar;
